/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Link } from '@backstage/core-components';
import { BackstageTheme } from '@backstage/theme';
import {
  Box,
  createStyles,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import React from 'react';
import pluralize from 'pluralize';
import TextLink from '../../text-link';
import { GroupMemberships } from '../../../context/userContext';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme: BackstageTheme) =>
  createStyles({
    card: {
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[2],
      borderRadius: '4px',
      padding: theme.spacing(2),
      color: '#fff',
      transition: `${theme.transitions.duration.standard}ms`,
      '&:hover': {
        boxShadow: theme.shadows[4],
      },
      height: '100%',
    },
    bold: {
      fontWeight: theme.typography.fontWeightBold,
    },
    entityTypeBox: {
      background: (props: { type: string; index: number }) =>
        theme.getPageTheme({ themeId: `homePageUser${props.index}` })
          .backgroundImage,
    },
    entityLink: {
      '&:hover': {
        textDecoration: 'none',
      },
    },
  }),
);

type ComponentGridProp = {
  counter: number;
  type?: string;
  kind?: string;
  url: string;
};

const EntityCountTile = ({
  counter,
  type,
  kind,
  url,
  index,
}: {
  counter: number;
  type?: string;
  kind?: string;
  url: string;
  index: number;
}) => {
  const classes = useStyles({ type: type ?? kind, index: index });

  const rawTitle = type ?? kind;

  return (
    <Link to={url} variant="body2" className={classes.entityLink}>
      <Box
        className={`${classes.card} ${classes.entityTypeBox}`}
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <Typography className={classes.bold} variant="h6">
          {counter}
        </Typography>
        <Typography className={classes.bold} variant="h6">
          {pluralize(rawTitle.toLocaleUpperCase('en-US'), counter)}
        </Typography>
        {type && <Typography variant="subtitle1">{kind}</Typography>}
      </Box>
    </Link>
  );
};

export const ComponentsGrid = ({
  userContextObj,
  groupMemberships,
}: {
  userContextObj: ComponentGridProp[];
  groupMemberships: GroupMemberships[];
}) => {
  if (groupMemberships.length === 0) {
    return (
      <Grid container>
        <Grid item xs={12} md={12} lg={12}>
          <Alert severity="warning">
            Your team is not registered with the{' '}
            <TextLink
              href="https://ablcode.visualstudio.com/DefaultCollection/Polaris/_git/platform-registration?path=/onboarding/squads.yaml"
              color="primary"
              external
            >
              Squad Registry
            </TextLink>
            . Open up a PR against the Squad Registry manifest to add your
            squad.
          </Alert>
        </Grid>
      </Grid>
    );
  }

  let numberOfRegistered: number = 0;
  userContextObj.forEach(element => {
    numberOfRegistered = numberOfRegistered + element.counter;
  });

  if (numberOfRegistered === 0) {
    return (
      <Grid container>
        <Grid item xs={12} md={12} lg={12}>
          <Alert severity="warning">
            You have no components registered in the developer portal. Follow{' '}
            <Link
              to="/docs/default/component/zephyr-techdocs/product-documentation/user/end-user/catalog-system/register-backstage-components/"
              color="primary"
              underline="none"
            >
              our documentation
            </Link>{' '}
            to get started registering your services, APIs and documentation
          </Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      {userContextObj
        .filter(c => c.counter > 0)
        .map((c, index) => (
          <Grid
            item
            xs={6}
            md={6}
            lg={3}
            style={{ flexGrow: 1, maxWidth: '100%' }}
            key={c.type ?? c.kind}
          >
            <EntityCountTile
              counter={c.counter}
              kind={c.kind}
              type={c.type}
              url={c.url}
              index={index}
            />
          </Grid>
        ))}
    </Grid>
  );
};
